import { render, type ComponentChildren } from 'preact'
import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { ClientAdmin, ErreurAdmin } from './api.ts'
import {
  appliquerEvenement,
  depuisConversation,
  type EtatConversation,
} from './conversation.ts'
import { installerStylesAdmin } from './styles.ts'
import { EasterEggDemo, type EtatEasterEggDemo } from './demo-easter-egg.tsx'
import { InventairePane } from './inventaire.tsx'
import type { ItemInventaire } from './inventaire-types.ts'
import { CataloguesPane } from './catalogues.tsx'
import { EvenementsPane } from './evenements.tsx'
import { FormulairesPane } from './formulaires.tsx'
import { DocumentsPane, stylesDocuments } from './documents.tsx'
import { FinancesPane } from './finances.tsx'
import { CourriersPane } from './courriels.tsx'
import { AccesPane } from './acces.tsx'
import { PortailPane } from './portail.tsx'
import { CalendrierPane, FicheDemande, InvitationsPane } from './gestion.tsx'
import {
  ETATS,
  filtrerDemandes,
  libelleEtat,
  dateLongue,
  periodeLisible,
} from './presentation.ts'
import type {
  ServiceAdmin,
  DemandeAdmin,
  EvenementConversation,
  TableauAdmin,
} from './types.ts'

type Onglet =
  | 'accueil'
  | 'inventaire'
  | 'catalogues'
  | 'portail'
  | 'evenements'
  | 'formulaires'
  | 'documents'
  | 'demandes'
  | 'calendrier'
  | 'finances'
  | 'acces'
  | 'reglages'
  | 'connexions'
  | 'conversation'
  | 'apercu'
  | 'integration'
  | 'consignes'
  | 'courriers'
const SECTIONS: Array<{
  cle: Onglet
  titre: string
  icone: string
  description: string
  groupe?: 'principal' | 'administration'
  permission?: string
}> = [
  {
    cle: 'accueil',
    titre: 'Accueil',
    icone: 'grille',
    description: 'Une vue claire sur ce qui compte aujourd’hui.',
    groupe: 'principal',
  },
  {
    cle: 'inventaire',
    titre: 'Inventaire',
    icone: 'grille',
    description:
      'Vos éléments réservables, leurs horaires, leurs tarifs et leurs comptes GL.',
    groupe: 'principal',
    permission: 'services.lire',
  },
  {
    cle: 'evenements',
    titre: 'Parcours clients',
    icone: 'evenement',
    description:
      'Choisissez un élément à réserver, composez son parcours et publiez-le.',
    groupe: 'principal',
    permission: 'services.lire',
  },
  {
    cle: 'catalogues',
    titre: 'Catalogues',
    icone: 'lien',
    description:
      'Regroupez vos espaces et équipements pour guider vos clients vers la bonne réservation.',
    groupe: 'principal',
    permission: 'services.lire',
  },
  {
    cle: 'portail',
    titre: 'Portail client',
    icone: 'personnes',
    description: 'Les contenus abonnés et les réservations prépayées de vos clients.',
    groupe: 'principal',
    permission: 'services.modifier',
  },
  {
    cle: 'demandes',
    titre: 'Réservations',
    icone: 'calendrier',
    description: 'Retrouvez chaque demande et donnez-lui suite.',
    groupe: 'principal',
    permission: 'demandes.lire',
  },
  {
    cle: 'calendrier',
    titre: 'Réservations',
    icone: 'calendrier',
    description: 'Vos espaces et vos activités, dans un même calendrier.',
    permission: 'demandes.lire',
  },
  {
    cle: 'finances',
    titre: 'Finances',
    icone: 'finance',
    description:
      'Des montants clairs. Des exports prêts pour votre comptabilité.',
    groupe: 'principal',
    permission: 'finances.lire',
  },
  {
    cle: 'formulaires',
    titre: 'Formulaires',
    icone: 'grille',
    description:
      'Des questions utiles, des modèles réutilisables. Chaque parcours a son formulaire.',
    permission: 'services.lire',
  },
  {
    cle: 'documents',
    titre: 'Documents',
    icone: 'lien',
    description:
      'Votre identité, vos contrats et les documents signés, au même endroit.',
    permission: 'services.lire',
  },
  {
    cle: 'acces',
    titre: 'Équipe',
    icone: 'personnes',
    description: 'Les bonnes personnes, avec les bons accès.',
    groupe: 'administration',
    permission: 'acces.gerer',
  },
  {
    cle: 'reglages',
    titre: 'Réglages',
    icone: 'reglages',
    description: 'Votre organisation, vos préférences et vos connexions.',
    groupe: 'administration',
  },
  {
    cle: 'connexions',
    titre: 'Connexions',
    icone: 'connexions',
    description: 'Reliez Funo à vos outils, en gardant le contrôle.',
    permission: 'integrations.gerer',
  },
  {
    cle: 'conversation',
    titre: 'Assistant',
    icone: 'sparkles',
    description: 'Décrivez votre besoin. Nous préparons la suite avec vous.',
    permission: 'conversation.utiliser',
  },
  {
    cle: 'apercu',
    titre: 'Aperçu',
    icone: 'oeil',
    description: 'Vivez le parcours de vos participants avant de le publier.',
    permission: 'services.modifier',
  },
  {
    cle: 'integration',
    titre: 'Sur votre site',
    icone: 'lien',
    description: 'Un lien à partager ou une réservation intégrée à votre site.',
    permission: 'services.lire',
  },
  {
    cle: 'courriers',
    titre: 'Courriels',
    icone: 'courriel',
    description:
      'Des messages attentionnés, aux couleurs de votre organisation.',
    groupe: 'principal',
    permission: 'services.lire',
  },
  {
    cle: 'consignes',
    titre: 'Mise en service',
    icone: 'reglages',
    description: 'Les dernières étapes pour accueillir vos participants.',
    permission: 'integrations.gerer',
  },
]
function ongletDepuisHash(): Onglet {
  return typeof window !== 'undefined'
    ? (SECTIONS.find(
        (s) =>
          s.cle ===
          ({ parcours: 'evenements', catalogue: 'catalogues' }[
            window.location.hash.slice(1)
          ] ?? window.location.hash.slice(1)),
      )?.cle ?? 'accueil')
    : 'accueil'
}
const VIDE: TableauAdmin = {
  organisation: { id: '', nom: 'Votre organisation' },
  services: [],
  demandes: [],
  consignes: [],
  courriers: [],
}
function erreurLisible(cause: unknown): string {
  return cause instanceof ErreurAdmin || cause instanceof Error
    ? cause.message
    : 'Une erreur inattendue est survenue.'
}
function Icone({ nom, taille = 20 }: { nom: string; taille?: number }) {
  const formes: Record<string, ComponentChildren> = {
    menu: <path d="M4 6h16M4 12h16M4 18h16" />,
    fermer: <path d="m6 6 12 12M6 18 18 6" />,
    chevron: <path d="m9 5 7 7-7 7" />,
    liste: (
      <>
        <path d="M8 6h12M8 12h12M8 18h12" />
        <circle cx="4" cy="6" r=".5" />
        <circle cx="4" cy="12" r=".5" />
        <circle cx="4" cy="18" r=".5" />
      </>
    ),
    recherche: (
      <>
        <circle cx="10.5" cy="10.5" r="6.5" />
        <path d="m16 16 5 5" />
      </>
    ),
    personnes: (
      <>
        <circle cx="9" cy="8" r="3" />
        <path d="M3 20v-2a6 6 0 0 1 12 0v2M16 5a3 3 0 0 1 0 6m1 3a5 5 0 0 1 4 4v2" />
      </>
    ),
    finance: (
      <>
        <rect x="3" y="4" width="18" height="16" rx="3" />
        <path d="M3 9h18M7 15h3m4 0h3" />
      </>
    ),
    connexions: (
      <>
        <rect x="2" y="8" width="7" height="8" rx="2" />
        <rect x="15" y="8" width="7" height="8" rx="2" />
        <path d="M9 12h6M5.5 5v3m0 8v3m13-14v3m0 8v3" />
      </>
    ),
    evenement: (
      <>
        <rect x="4" y="4" width="16" height="17" rx="3" />
        <path d="M8 2v4m8-4v4M4 10h16m-12 5h3m2 0h3m-8 3h3" />
      </>
    ),
    sparkles: (
      <>
        <path d="m12 3 2.4 6.6L21 12l-6.6 2.4L12 21l-2.4-6.6L3 12l6.6-2.4L12 3ZM20 2v4m-2-2h4" />
      </>
    ),

    grille: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
      </>
    ),
    calendrier: (
      <>
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M7 3v4m10-4v4M3 11h18m-13 5h3m3 0h2" />
      </>
    ),
    conversation: (
      <path d="M21 11a8 8 0 0 1-8 8H8l-5 3V11a8 8 0 0 1 8-8h2a8 8 0 0 1 8 8ZM7 9h10M7 13h6" />
    ),
    oeil: (
      <>
        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ),
    lien: (
      <>
        <path d="m10 13 4-4m-5 7-2 2a4 4 0 0 1-6-6l4-4a4 4 0 0 1 6 0m2 0 2-2a4 4 0 0 1 6 6l-4 4a4 4 0 0 1-6 0" />
      </>
    ),
    courriel: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="m3 6 9 7 9-7" />
      </>
    ),
    reglages: (
      <>
        <path d="M4 7h16M4 17h16" />
        <circle cx="8" cy="7" r="3" />
        <circle cx="16" cy="17" r="3" />
      </>
    ),
    fleche: <path d="M5 12h14m-6-6 6 6-6 6" />,
    plus: <path d="M12 5v14M5 12h14" />,
    verifier: <path d="m5 12 4 4L19 6" />,
    horloge: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    externe: (
      <>
        <path d="M14 3h7v7m0-7L10 14M10 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-5" />
      </>
    ),
  }
  return (
    <svg
      width={taille}
      height={taille}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.7"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      {formes[nom] ?? formes.grille}
    </svg>
  )
}

export function App({
  baseUrl,
  conversationId,
  loginMode,
}: {
  baseUrl: string
  conversationId: string
  loginMode?: 'central' | 'local'
}) {
  const client = useMemo(() => new ClientAdmin(baseUrl), [baseUrl])
  const [tableau, setTableau] = useState<TableauAdmin>(VIDE)
  const [easterEggDemo, setEasterEggDemo] = useState<EtatEasterEggDemo>({ disponible: false, actif: false })
  const revisionEasterEgg = useRef(0), desactivationEasterEgg = useRef<number | null>(null)
  const [onglet, setOnglet] = useState<Onglet>(() => ongletDepuisHash())
  const [service, setService] = useState('')
  const [articleInitial, setArticleInitial] = useState<
    ItemInventaire | undefined
  >(undefined)
  const [selectionInventaire, setSelectionInventaire] = useState<
    string | undefined
  >(undefined)
  const [chargement, setChargement] = useState(true)
  const [connexionRequise, setConnexionRequise] = useState(false)
  const [organisationAbsente, setOrganisationAbsente] = useState(false)
  const [mutation, setMutation] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [notification, setNotification] = useState('')
  const [actualise, setActualise] = useState(false)
  const [menuOuvert, setMenuOuvert] = useState(false)
  const [rechercheOuverte, setRechercheOuverte] = useState(false)
  const [intentionCreation, setIntentionCreation] = useState(0)
  const [editionEvenement, setEditionEvenement] = useState(false)
  const [intentionOuverture, setIntentionOuverture] = useState(0)
  const [detailId, setDetailId] = useState<string | null>(null)
  const [filtreInitial, setFiltreInitial] = useState('')
  const [revisionApercu, setRevisionApercu] = useState(0)
  const [action, setAction] = useState<{
    type: 'publier' | 'refuser' | 'approuver'
    id: string
    titre: string
  } | null>(null)
  const [motif, setMotif] = useState('')
  const [demonstrationConfirmee, setDemonstrationConfirmee] = useState(false)
  const [conversationCourante, setConversationCourante] =
    useState(conversationId)
  const [conversations, setConversations] = useState<
    Array<{ id: string; titre: string }>
  >([])
  const [conversationEnCours, setConversationEnCours] = useState(false)
  const menuBouton = useRef<HTMLButtonElement>(null),
    lateral = useRef<HTMLElement>(null)
  const dialogue = useRef<HTMLDialogElement>(null),
    titre = useRef<HTMLHeadingElement>(null),
    requete = useRef(0)
  const permissions = tableau.moi?.permissions ?? []
  const peut = (permission: string) => permissions.includes(permission)
  const section = SECTIONS.find((s) => s.cle === onglet) ?? SECTIONS[0]!
  const selection = tableau.services.find((s) => s.cle === service)
  const publicationSimulee =
    action?.type === 'publier' &&
    tableau.services.find((s) => s.cle === action.id)?.paiement_en_demonstration
  const attentes = tableau.demandes.filter(
    (d) => d.etat === 'en_attente_approbation',
  )
  const fuseau = tableau.organisation.fuseau ?? 'America/Toronto'
  const aujourdHui = new Intl.DateTimeFormat('fr-CA', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: fuseau,
  }).format(new Date())
  const sectionsVisibles = SECTIONS.filter(
    (s) => !s.permission || peut(s.permission) || (s.cle === 'courriers' && peut('demandes.gerer')) || (s.cle === 'portail' && peut('finances.modifier')),
  )
  const naviguer = (prochain: Onglet) => {
    setOnglet(prochain)
    setMenuOuvert(false)
    if (typeof window !== 'undefined') {
      if (window.location.hash !== `#${prochain}`)
        window.history.pushState(null, '', `#${prochain}`)
      window.requestAnimationFrame(() => titre.current?.focus())
    }
  }
  const actualiser = async (signal?: AbortSignal) => {
    const numero = ++requete.current
    try {
      const prochain = await client.tableau(signal)
      if (signal?.aborted || numero !== requete.current) return
      setTableau({
        ...prochain,
        organisation: prochain.organisation ?? VIDE.organisation,
        services: prochain.services ?? [],
        demandes: prochain.demandes ?? [],
        consignes: prochain.consignes ?? [],
        courriers: prochain.courriers ?? [],
      })
      setService((courant) =>
        prochain.services?.some((s) => s.cle === courant) ? courant : '',
      )
      setActualise(true)
      setOrganisationAbsente(false)
      setConnexionRequise(false)
    } catch (cause) {
      if (signal?.aborted || numero !== requete.current) return
      setActualise(false)
      if (cause instanceof ErreurAdmin && cause.statut === 401)
        setConnexionRequise(true)
      else if (cause instanceof ErreurAdmin && cause.statut === 404)
        setOrganisationAbsente(true)
      else setErreur(erreurLisible(cause))
    } finally {
      if (!signal?.aborted && numero === requete.current) setChargement(false)
    }
  }
  useEffect(() => {
    ++revisionEasterEgg.current
    desactivationEasterEgg.current = null
    setEasterEggDemo({ disponible: false, actif: false })
  }, [client, tableau.organisation.id, connexionRequise])
  useEffect(() => {
    if (!tableau.organisation.id || connexionRequise) return
    const controleur = new AbortController()
    const visiter = async () => {
      if (document.visibilityState === 'hidden' || desactivationEasterEgg.current !== null) return
      const revision = ++revisionEasterEgg.current
      try {
        const etat = await client.visiterDemo(controleur.signal)
        if (!controleur.signal.aborted && revision === revisionEasterEgg.current) setEasterEggDemo(etat)
      } catch {
        // Une surprise visuelle indisponible ne bloque jamais le travail.
      }
    }
    void visiter()
    window.addEventListener('focus', visiter)
    document.addEventListener('visibilitychange', visiter)
    return () => {
      controleur.abort()
      window.removeEventListener('focus', visiter)
      document.removeEventListener('visibilitychange', visiter)
    }
  }, [client, tableau.organisation.id, connexionRequise, onglet])
  const desactiverEasterEgg = async () => {
    const revision = ++revisionEasterEgg.current
    desactivationEasterEgg.current = revision
    try {
      const etat = await client.desactiverEasterEggDemo()
      if (revision !== revisionEasterEgg.current) return
      setEasterEggDemo(etat)
      setNotification('Les couleurs habituelles sont de retour.')
      window.requestAnimationFrame(() => titre.current?.focus())
    } finally {
      if (desactivationEasterEgg.current === revision) desactivationEasterEgg.current = null
    }
  }
  useEffect(() => {
    const c = new AbortController()
    void actualiser(c.signal)
    const timer = window.setInterval(() => void actualiser(c.signal), 30_000)
    return () => {
      c.abort()
      ++requete.current
      window.clearInterval(timer)
    }
  }, [client])
  useEffect(() => {
    const retour = () => {
      setOnglet(ongletDepuisHash())
      setMenuOuvert(false)
    }
    const raccourci = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setRechercheOuverte((v) => !v)
      }
      if (e.key === 'Escape')
        setMenuOuvert((ouvert) => {
          if (ouvert)
            window.requestAnimationFrame(() => menuBouton.current?.focus())
          return false
        })
    }
    window.addEventListener('popstate', retour)
    window.addEventListener('hashchange', retour)
    window.addEventListener('keydown', raccourci)
    return () => {
      window.removeEventListener('popstate', retour)
      window.removeEventListener('hashchange', retour)
      window.removeEventListener('keydown', raccourci)
    }
  }, [])
  useEffect(() => {
    if (menuOuvert)
      lateral.current?.querySelector<HTMLAnchorElement>('a')?.focus()
  }, [menuOuvert])
  useEffect(() => {
    if (!notification) return
    const id = window.setTimeout(() => setNotification(''), 6500)
    return () => window.clearTimeout(id)
  }, [notification])
  useEffect(() => {
    if (action && dialogue.current && !dialogue.current.open)
      dialogue.current.showModal()
  }, [action])
  useEffect(() => {
    if (onglet !== 'conversation' || !peut('conversation.utiliser')) return
    const c = new AbortController()
    void client
      .conversations(c.signal)
      .then((r) => setConversations(r.conversations))
      .catch(() => {})
    return () => c.abort()
  }, [client, onglet, tableau])
  const agir = async (
    executer: () => Promise<unknown>,
    message: string,
    apres?: () => void,
  ) => {
    if (mutation) return false
    setMutation(true)
    setErreur(null)
    try {
      await executer()
      apres?.()
      setNotification(message)
      await actualiser()
      return true
    } catch (cause) {
      if (cause instanceof ErreurAdmin && cause.statut === 401)
        setConnexionRequise(true)
      else setErreur(erreurLisible(cause))
      return false
    } finally {
      setMutation(false)
    }
  }
  const creer = (article?: ItemInventaire) => {
    setArticleInitial(article)
    setService('')
    setIntentionCreation((v) => v + 1)
    naviguer('evenements')
  }
  const ouvrirEvenement = (cle: string) => {
    setArticleInitial(undefined)
    setService(cle)
    setIntentionOuverture((v) => v + 1)
    naviguer('evenements')
  }
  const voirDemandes = (etat = '') => {
    setFiltreInitial(etat)
    naviguer('demandes')
  }
  const fermerDialogue = () => {
    dialogue.current?.close()
    setAction(null)
    setMotif('')
    setDemonstrationConfirmee(false)
  }
  const confirmer = async (e: Event) => {
    e.preventDefault()
    if (!action || mutation) return
    const courant = action,
      raison = motif,
      demo = demonstrationConfirmee
    const succes = await agir(
      () =>
        courant.type === 'publier'
          ? client.publier(courant.id, 'Publier cette version', demo)
          : client.decision(courant.id, courant.type, raison),
      courant.type === 'publier'
        ? 'Votre événement est en ligne.'
        : 'La décision est enregistrée.',
    )
    if (succes) fermerDialogue()
  }
  const navActive =
    onglet === 'calendrier'
      ? 'demandes'
      : ['integration', 'consignes', 'connexions'].includes(onglet)
        ? 'reglages'
        : onglet === 'apercu'
          ? 'evenements'
          : onglet
  if (connexionRequise) return <Connexion client={client} loginMode={loginMode} />
  if (organisationAbsente)
    return (
      <main class="admin__connexion">
        <img
          class="ux-login-logo"
          src="/brand/funo-logo-vertical-couleur-fond-clair.svg"
          alt="Funo"
          width="168"
          height="162"
        />
        <h1>Bienvenue chez vous.</h1>
        <p>
          Votre organisation attend sa configuration. La personne responsable de
          l’installation doit renseigner son nom et votre adresse courriel.
        </p>
        <button
          class="admin__primaire"
          type="button"
          onClick={() => void actualiser()}
        >
          Vérifier la configuration
        </button>
      </main>
    )
  return (
    <div class={`admin ux-app${menuOuvert ? ' ux-app--menu' : ''}${easterEggDemo.disponible && easterEggDemo.actif ? ' ux-portail--arc-en-ciel' : ''}`}>
      <a class="admin__evitement" href="#admin-contenu">
        Aller au contenu
      </a>
      {menuOuvert ? (
        <button
          class="ux-voile"
          aria-label="Fermer le menu"
          onClick={() => {
            setMenuOuvert(false)
            window.requestAnimationFrame(() => menuBouton.current?.focus())
          }}
        />
      ) : null}
      <aside
        id="navigation-principale"
        ref={lateral}
        class="admin__lateral"
        aria-label="Navigation principale"
        onKeyDown={(e) => {
          if (!menuOuvert || e.key !== 'Tab') return
          const controles = Array.from(
            lateral.current?.querySelectorAll<HTMLElement>('a,button') ?? [],
          )
          const premier = controles[0],
            dernier = controles.at(-1)
          if (e.shiftKey && document.activeElement === premier) {
            e.preventDefault()
            dernier?.focus()
          } else if (!e.shiftKey && document.activeElement === dernier) {
            e.preventDefault()
            premier?.focus()
          }
        }}
      >
        <a
          class="ux-marque"
          href="#accueil"
          onClick={(e) => {
            e.preventDefault()
            naviguer('accueil')
          }}
        >
          <img
            class="ux-logo"
            src="/brand/funo-logo-horizontal-couleur-fond-clair.svg"
            alt="Funo"
            width="136"
            height="40"
          />
          <span class="ux-marque-description">Votre espace de gestion</span>
        </a>
        <div class="ux-organisation">
          <span class="admin__avatar">
            {tableau.organisation.nom.slice(0, 1)}
          </span>
          <div>
            <strong>{tableau.organisation.nom}</strong>
            <small>
              {tableau.moi?.mode === 'developpement'
                ? 'Espace de démonstration'
                : 'Espace de votre équipe'}
            </small>
          </div>
        </div>
        <nav class="admin__navigation" aria-label="Sections d’administration">
          {(['principal', 'administration'] as const).map((groupe) => (
            <div class="ux-nav-groupe" key={groupe}>
              {groupe === 'administration' ? (
                <span class="ux-nav-label">Organisation</span>
              ) : null}
              {sectionsVisibles
                .filter((s) => s.groupe === groupe)
                .map((s) => (
                  <div class="ux-nav-entree" key={s.cle}>
                    <button
                      type="button"
                      key={s.cle}
                      aria-label={s.titre}
                      aria-current={
                        navActive === s.cle ||
                        (s.cle === 'evenements' &&
                          ['formulaires', 'documents'].includes(onglet))
                          ? 'page'
                          : undefined
                      }
                      onClick={() => naviguer(s.cle)}
                    >
                      <Icone nom={s.icone} />
                      <span>{s.titre}</span>
                      {s.cle === 'demandes' && attentes.length > 0 ? (
                        <b class="ux-badge">{attentes.length}</b>
                      ) : null}
                    </button>
                    {s.cle === 'evenements' &&
                    ['evenements', 'formulaires', 'documents'].includes(
                      onglet,
                    ) ? (
                      <div class="ux-nav-enfants">
                        {[
                          {
                            cle: 'evenements',
                            titre: 'Parcours et publication',
                          },
                          { cle: 'formulaires', titre: 'Formulaires' },
                          { cle: 'documents', titre: 'Documents et contrats' },
                        ].map((v) => (
                          <button
                            type="button"
                            aria-current={onglet === v.cle ? 'page' : undefined}
                            onClick={() => naviguer(v.cle as Onglet)}
                          >
                            {v.titre}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
            </div>
          ))}
        </nav>
        <div class="ux-sidebar-bottom">
          {peut('conversation.utiliser') ? (
            <button
              class="ux-assistant-lien"
              type="button"
              onClick={() => naviguer('conversation')}
            >
              <Icone nom="sparkles" />
              <span>
                Un coup de main ?<small>Ouvrir l’assistant</small>
              </span>
              <Icone nom="fleche" taille={15} />
            </button>
          ) : null}
          <a
            class="ux-site-lien"
            href={`${baseUrl}/`}
            target="_blank"
            rel="noreferrer"
          >
            Voir le portail public <Icone nom="externe" taille={14} />
          </a>
          <div class="ux-statut-connexion">
            <span
              class={`admin__point${actualise ? '' : ' admin__point--attente'}`}
            />
            {actualise ? 'À jour' : 'Connexion en cours'}
          </div>
        </div>
      </aside>
      <div class="admin__espace" inert={menuOuvert}>
        <header class="admin__barre">
          <div class="ux-breadcrumb">
            <button
              ref={menuBouton}
              class="ux-menu-bouton"
              aria-controls="navigation-principale"
              aria-label="Ouvrir le menu"
              aria-expanded={menuOuvert}
              onClick={() => setMenuOuvert((v) => !v)}
            >
              <Icone nom="menu" />
            </button>
            <span>{tableau.organisation.nom}</span>
            <Icone nom="chevron" taille={12} />
            <strong>{section.titre}</strong>
          </div>
          <div class="ux-header-outils">
            <button
              class="ux-recherche-bouton"
              onClick={() => setRechercheOuverte(true)}
              aria-label="Rechercher dans le portail"
            >
              <Icone nom="recherche" taille={18} />
              <span>Rechercher</span>
              <kbd>⌘ K</kbd>
            </button>
            <details class="ux-compte">
              <summary aria-label="Mon compte">
                <span class="admin__avatar admin__avatar--petit">
                  {(
                    tableau.moi?.nom ??
                    tableau.moi?.courriel ??
                    tableau.organisation.nom
                  )
                    .slice(0, 1)
                    .toUpperCase()}
                </span>
              </summary>
              <div class="ux-compte-menu">
                <strong>{tableau.moi?.nom || 'Mon compte'}</strong>
                <small>{tableau.moi?.courriel}</small>
                {(tableau.organisations?.length ?? 0) > 1 ? (
                  <nav class="ux-compte-organisations" aria-label="Changer d’organisation">
                    <small>Vos organisations</small>
                    {tableau.organisations!.map((o) => (
                      <button
                        type="button"
                        key={o.id}
                        aria-current={o.courante ? 'true' : undefined}
                        disabled={o.courante}
                        onClick={() =>
                          void client
                            .choisirOrganisation(o.id)
                            .then(() => window.location.assign('/admin'))
                            .catch((e) => setErreur(erreurLisible(e)))
                        }
                      >
                        {o.courante ? `✓ ${o.nom}` : o.nom}
                      </button>
                    ))}
                  </nav>
                ) : null}
                {tableau.moi?.mode === 'session' ? (
                  <button
                    type="button"
                    onClick={() =>
                      void client
                        .sortir()
                        .then(() => setConnexionRequise(true))
                        .catch((e) => setErreur(erreurLisible(e)))
                    }
                  >
                    Se déconnecter
                  </button>
                ) : (
                  <p>Espace de démonstration local.</p>
                )}
              </div>
            </details>
          </div>
        </header>
        <main id="admin-contenu" class="admin__contenu" aria-busy={chargement}>
          <EasterEggDemo etat={easterEggDemo} onDesactiver={desactiverEasterEgg} />
          <div class="ux-page-entete">
            <div>
              {onglet === 'accueil' ? (
                <p class="ux-date">{aujourdHui}</p>
              ) : null}
              <h1 ref={titre} tabIndex={-1}>
                {section.titre}
              </h1>
              <p>{section.description}</p>
            </div>
            {peut('services.modifier') &&
            ['accueil', 'evenements'].includes(onglet) &&
            !(onglet === 'evenements' && editionEvenement) ? (
              <button
                type="button"
                class="admin__primaire"
                onClick={() => creer()}
              >
                <Icone nom="plus" taille={17} />
                Créer un parcours
              </button>
            ) : null}
            {onglet === 'conversation' ? (
              <button
                type="button"
                class="admin__secondaire"
                disabled={conversationEnCours}
                onClick={() => setConversationCourante(crypto.randomUUID())}
              >
                <Icone nom="plus" taille={17} />
                Nouvelle conversation
              </button>
            ) : null}
          </div>
          {erreur ? (
            <div class="admin__message admin__message--erreur" role="alert">
              <span>{erreur}</span>
              <button
                type="button"
                onClick={() => {
                  setErreur(null)
                  void actualiser()
                }}
              >
                Réessayer
              </button>
            </div>
          ) : null}
          {chargement ? (
            <div
              class="ux-skeleton"
              role="status"
              aria-label="Chargement du portail"
            >
              <span />
              <span />
              <span />
              <div />
            </div>
          ) : (
            <>
              {tableau.mode &&
              Object.values(tableau.mode).some((m) => m.startsWith('simul')) ? (
                <details class="ux-mode-demo">
                  <summary>
                    <span class="ux-demo-point" />
                    <strong>Démonstration</strong>
                    <span>Explorez les parcours en toute simplicité.</span>
                    <Icone nom="chevron" taille={13} />
                  </summary>
                  <p>
                    {[
                      tableau.mode.conversation.startsWith('simul')
                        ? 'L’assistant utilise des exemples.'
                        : '',
                      tableau.mode.courriel.startsWith('simul')
                        ? 'Les courriels ne sont pas envoyés.'
                        : '',
                      tableau.mode.paiement.startsWith('simul')
                        ? 'Aucun paiement réel n’est encaissé.'
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  </p>
                </details>
              ) : null}
              {section.permission && !peut(section.permission) && !(onglet === 'courriers' && peut('demandes.gerer')) && !(onglet === 'portail' && peut('finances.modifier')) ? (
                <EtatVide
                  titre="Cet espace n’est pas accessible avec votre rôle"
                  texte="La personne qui gère votre équipe peut ajuster vos accès."
                  action={() => naviguer('accueil')}
                  bouton="Revenir à l’accueil"
                />
              ) : (
                <>
                  {onglet === 'accueil' ? (
                    <>
                      <div class="ux-resume">
                        {peut('services.lire') ? (
                          <Stat
                            titre="En ligne"
                            valeur={
                              tableau.services.filter(
                                (s) => s.etat === 'publie',
                              ).length
                            }
                            detail="Événements et locations"
                            icone="evenement"
                            action={() => naviguer('evenements')}
                          />
                        ) : null}
                        {peut('demandes.lire') ? (
                          <>
                            <Stat
                              titre="À examiner"
                              valeur={attentes.length}
                              detail="Demandes d’approbation"
                              icone="horloge"
                              action={() =>
                                voirDemandes('en_attente_approbation')
                              }
                            />
                            <Stat
                              titre="Confirmées"
                              valeur={
                                tableau.demandes.filter(
                                  (d) => d.etat === 'confirmee',
                                ).length
                              }
                              detail="Parmi les 100 dernières réservations"
                              icone="verifier"
                              action={() => voirDemandes('confirmee')}
                            />
                          </>
                        ) : null}
                        {!peut('demandes.lire') && peut('finances.lire') ? (
                          <button
                            class="ux-acces-rapide"
                            type="button"
                            onClick={() => naviguer('finances')}
                          >
                            <Icone nom="finance" />
                            <strong>Vos rapports financiers</strong>
                            <span>Consulter, vérifier et exporter</span>
                            <Icone nom="fleche" />
                          </button>
                        ) : null}
                      </div>
                      <div class="ux-accueil-grille">
                        <section class="admin__panneau ux-evenements-accueil">
                          <div class="admin__section-entete">
                            <div>
                              <h2>Vos événements</h2>
                              <p>De l’idée aux premières inscriptions.</p>
                            </div>
                            {peut('services.lire') ? (
                              <button
                                class="admin__texte-bouton"
                                onClick={() => naviguer('evenements')}
                              >
                                Tout voir <Icone nom="chevron" taille={14} />
                              </button>
                            ) : null}
                          </div>
                          {tableau.services.length ? (
                            <div class="ux-liste-evenements">
                              {tableau.services.slice(0, 4).map((s, i) => (
                                <button
                                  class="ux-evenement-ligne"
                                  key={s.cle}
                                  onClick={() => ouvrirEvenement(s.cle)}
                                >
                                  <span
                                    class={`ux-vignette ux-vignette--${i % 3}`}
                                  >
                                    <Icone nom="evenement" taille={24} />
                                  </span>
                                  <span class="ux-evenement-description">
                                    <strong>{s.titre ?? s.nom ?? s.cle}</strong>
                                    <small>
                                      {s.a_un_brouillon
                                        ? 'Modifications en préparation'
                                        : s.etat === 'publie'
                                          ? 'Les réservations sont ouvertes'
                                          : 'Prêt à être préparé'}
                                    </small>
                                  </span>
                                  <span
                                    class={`ux-etat-publication ${s.etat === 'publie' ? 'ux-etat-publication--ouvert' : ''}`}
                                  >
                                    {s.etat === 'publie'
                                      ? 'En ligne'
                                      : 'Brouillon'}
                                  </span>
                                  <Icone nom="chevron" taille={16} />
                                </button>
                              ))}
                            </div>
                          ) : (
                            <EtatVide
                              titre={
                                peut('services.modifier')
                                  ? 'Votre premier événement commence ici'
                                  : 'Aucun événement à afficher'
                              }
                              texte="Choisissez un événement avec inscriptions ou une salle à réserver. Nous vous guidons pour la suite."
                              action={
                                peut('services.modifier') ? creer : undefined
                              }
                              bouton="Créer mon premier événement"
                            />
                          )}
                        </section>
                        <aside class="ux-prochaine-etape">
                          <span class="ux-icone-douce">
                            <Icone
                              nom={attentes.length ? 'horloge' : 'sparkles'}
                              taille={26}
                            />
                          </span>
                          <p class="ux-surtitle">VOTRE PROCHAINE ÉTAPE</p>
                          <h2>
                            {attentes.length
                              ? `${attentes.length} demande${attentes.length > 1 ? 's' : ''} à examiner.`
                              : 'Tout commence par une idée.'}
                          </h2>
                          <p>
                            {attentes.length
                              ? 'Vérifiez les renseignements et donnez votre réponse, au même endroit.'
                              : 'Un atelier, une salle ou une activité récurrente. Préparez un parcours clair pour vos participants.'}
                          </p>
                          {attentes.length ? (
                            <button
                              class="admin__secondaire"
                              type="button"
                              onClick={() =>
                                voirDemandes('en_attente_approbation')
                              }
                            >
                              Voir les demandes{' '}
                              <Icone nom="fleche" taille={16} />
                            </button>
                          ) : peut('services.modifier') ? (
                            <button
                              class="admin__secondaire"
                              type="button"
                              onClick={() => creer()}
                            >
                              Préparer un événement{' '}
                              <Icone nom="fleche" taille={16} />
                            </button>
                          ) : (
                            <button
                              class="admin__secondaire"
                              type="button"
                              onClick={() =>
                                naviguer(
                                  peut('finances.lire')
                                    ? 'finances'
                                    : 'reglages',
                                )
                              }
                            >
                              Ouvrir mon espace{' '}
                              <Icone nom="fleche" taille={16} />
                            </button>
                          )}
                        </aside>
                      </div>
                      {peut('demandes.lire') ? (
                        <section class="admin__panneau">
                          <div class="admin__section-entete">
                            <div>
                              <h2>Dernières réservations</h2>
                              <p>Retrouvez rapidement les demandes récentes.</p>
                            </div>
                            <button
                              class="admin__texte-bouton"
                              type="button"
                              onClick={() => voirDemandes()}
                            >
                              Tout voir <Icone nom="chevron" taille={14} />
                            </button>
                          </div>
                          <TableDemandes
                            demandes={tableau.demandes.slice(0, 5)}
                            services={tableau.services}
                            fuseau={fuseau}
                            ouvrir={setDetailId}
                          />
                        </section>
                      ) : null}
                    </>
                  ) : null}
                  {['demandes', 'calendrier'].includes(onglet) ? (
                    <>
                      <div class="ux-vue-barre">
                        <div
                          class="ux-segments"
                          aria-label="Affichage des réservations"
                        >
                          <button
                            type="button"
                            aria-pressed={onglet === 'demandes'}
                            onClick={() => naviguer('demandes')}
                          >
                            <Icone nom="liste" taille={16} />
                            Liste
                          </button>
                          <button
                            type="button"
                            aria-pressed={onglet === 'calendrier'}
                            onClick={() => naviguer('calendrier')}
                          >
                            <Icone nom="calendrier" taille={16} />
                            Calendrier
                          </button>
                        </div>
                        <span class="ux-caption">Heures de {fuseau}</span>
                      </div>
                      {onglet === 'demandes' ? (
                        <section class="admin__panneau">
                          <DemandesPane
                            key={filtreInitial}
                            demandes={tableau.demandes}
                            services={tableau.services}
                            initial={filtreInitial}
                            mutation={mutation}
                            fuseau={fuseau}
                            ouvrir={setDetailId}
                            decider={
                              peut('demandes.gerer')
                                ? (d, type) =>
                                    setAction({
                                      type,
                                      id: d.id,
                                      titre:
                                        d.demandeur ??
                                        d.courriel ??
                                        'cette réservation',
                                    })
                                : undefined
                            }
                          />
                        </section>
                      ) : (
                        <CalendrierPane
                          client={client}
                          services={tableau.services}
                          fuseau={fuseau}
                          ouvrir={setDetailId}
                          connexion={() => setConnexionRequise(true)}
                          actualisation={tableau}
                        />
                      )}
                    </>
                  ) : null}
                  {onglet === 'portail' ? <PortailPane baseUrl={baseUrl} permissions={permissions} onAuth={() => setConnexionRequise(true)} /> : null}
                  {onglet === 'finances' ? (
                    <FinancesPane
                      baseUrl={baseUrl}
                      permissions={permissions}
                      onAuth={() => setConnexionRequise(true)}
                      onOuvrirDemande={setDetailId}
                    />
                  ) : null}
                  {onglet === 'acces' || onglet === 'connexions' ? (
                    <AccesPane
                      key={onglet}
                      baseUrl={baseUrl}
                      permissions={permissions}
                      onAuth={() => setConnexionRequise(true)}
                      sectionInitiale={
                        onglet === 'connexions' ? 'integrations' : 'equipe'
                      }
                    />
                  ) : null}
                  {onglet === 'reglages' ? (
                    <div class="ux-reglages">
                      <section class="admin__panneau">
                        <div class="admin__section-entete">
                          <div>
                            <h2>Votre organisation</h2>
                            <p>Les renseignements de votre espace.</p>
                          </div>
                        </div>
                        <dl class="ux-fiche-organisation">
                          <dt>Organisation</dt>
                          <dd>{tableau.organisation.nom}</dd>
                          <dt>Fuseau horaire</dt>
                          <dd>{fuseau}</dd>
                          <dt>Adresse de contact</dt>
                          <dd>
                            {tableau.organisation.courriel || 'À renseigner'}
                          </dd>
                        </dl>
                      </section>
                      <section class="admin__panneau">
                        <div class="admin__section-entete">
                          <h2>Pour aller plus loin</h2>
                        </div>
                        {(
                          [
                            {
                              cle: 'integration',
                              titre: 'Sur votre site',
                              description:
                                'Publiez un lien ou intégrez les réservations à votre site.',
                              icone: 'lien',
                              permission: 'services.lire',
                            },
                            {
                              cle: 'connexions',
                              titre: 'Applications et connexions',
                              description:
                                'Gérez vos clés API et connectez un assistant avec MCP.',
                              icone: 'connexions',
                              permission: 'integrations.gerer',
                            },
                            {
                              cle: 'courriers',
                              titre: 'Courriels et identité',
                              description:
                                'Personnalisez vos modèles et consultez les courriels envoyés.',
                              icone: 'courriel',
                              permission: 'services.lire',
                            },
                            {
                              cle: 'consignes',
                              titre: 'Mise en service',
                              description:
                                'Retrouvez les étapes de configuration restantes.',
                              icone: 'reglages',
                              permission: 'integrations.gerer',
                            },
                          ] as const
                        )
                          .filter((r) => peut(r.permission))
                          .map((r) => (
                            <button
                              class="ux-reglage-ligne"
                              type="button"
                              key={r.cle}
                              onClick={() => naviguer(r.cle)}
                            >
                              <span class="ux-icone-douce">
                                <Icone nom={r.icone} />
                              </span>
                              <span>
                                <strong>{r.titre}</strong>
                                <small>{r.description}</small>
                              </span>
                              <Icone nom="chevron" taille={16} />
                            </button>
                          ))}
                      </section>
                    </div>
                  ) : null}
                  {onglet === 'apercu' ? (
                    <section class="admin__panneau">
                      <div class="admin__section-entete">
                        <div class="admin__champ">
                          <label for="admin-service">
                            Événement à prévisualiser
                          </label>
                          <select
                            id="admin-service"
                            value={service}
                            onInput={(e) => setService(e.currentTarget.value)}
                          >
                            <option value="">Choisir un événement</option>
                            {tableau.services.map((s) => (
                              <option value={s.cle}>
                                {s.titre ?? s.nom ?? s.cle}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div class="admin__actions">
                          {service ? (
                            <a href={client.urlConfiguration(service)}>
                              Exporter la configuration
                            </a>
                          ) : null}
                          {peut('services.modifier') ? (
                            <>
                              <button
                                class="admin__secondaire"
                                disabled={!service || mutation}
                                onClick={() =>
                                  void agir(
                                    () => client.apercu(service),
                                    'Votre aperçu est prêt.',
                                    () => setRevisionApercu((v) => v + 1),
                                  )
                                }
                              >
                                Actualiser l’aperçu
                              </button>
                              <button
                                class="admin__primaire"
                                disabled={
                                  !selection?.a_un_brouillon || mutation
                                }
                                onClick={() =>
                                  setAction({
                                    type: 'publier',
                                    id: service,
                                    titre: selection?.titre ?? service,
                                  })
                                }
                              >
                                Publier
                              </button>
                            </>
                          ) : null}
                        </div>
                      </div>
                      <ApercuPane
                        key={`${service}-${revisionApercu}`}
                        baseUrl={baseUrl}
                        service={service}
                      />
                      {selection?.etat === 'publie' && selection.acces_sur_invitation ? (
                        <InvitationsPane
                          client={client}
                          service={selection.cle}
                          fuseau={fuseau}
                          modifiable={peut('services.modifier')}
                          connexion={() => setConnexionRequise(true)}
                        />
                      ) : null}
                    </section>
                  ) : null}
                  {onglet === 'integration' ? (
                    <section class="admin__panneau">
                      <IntegrationPane services={tableau.services} />
                    </section>
                  ) : null}
                  {onglet === 'consignes' ? (
                    <ConsignesPane consignes={tableau.consignes} />
                  ) : null}
                </>
              )}
              {peut('services.lire') || peut('demandes.gerer') ? (
                <div hidden={onglet !== 'courriers'}>
                  <CourriersPane
                    client={client}
                    permissions={permissions}
                    brouillonCle={`${baseUrl}|${tableau.organisation.id}|${tableau.moi?.membre_id ?? ''}`}
                    courriers={tableau.courriers}
                    fuseau={fuseau}
                    visible={onglet === 'courriers'}
                    onAuth={() => setConnexionRequise(true)}
                  />
                </div>
              ) : null}
              {peut('services.lire') ? (
                <>
                  <div hidden={onglet !== 'inventaire'}>
                    <InventairePane
                      baseUrl={baseUrl}
                      permissions={permissions}
                      visible={onglet === 'inventaire'}
                      onAuth={() => setConnexionRequise(true)}
                      selectionInitiale={selectionInventaire}
                      onParcours={(article) => creer(article)}
                      onOuvrirParcours={ouvrirEvenement}
                    />
                  </div>
                  <div hidden={onglet !== 'catalogues'}>
                    <CataloguesPane
                      baseUrl={baseUrl}
                      permissions={permissions}
                      visible={onglet === 'catalogues'}
                      onAuth={() => setConnexionRequise(true)}
                    />
                  </div>
                </>
              ) : null}
              {peut('services.lire') ? (
                <div hidden={onglet !== 'evenements'}>
                  <EvenementsPane
                    baseUrl={baseUrl}
                    permissions={permissions}
                    onAuth={() => setConnexionRequise(true)}
                    onChange={() => void actualiser()}
                    onEditionChange={setEditionEvenement}
                    articleInitial={articleInitial}
                    onInventaire={(id) => {
                      setSelectionInventaire(id)
                      naviguer('inventaire')
                    }}
                    intentionOuverture={intentionOuverture}
                    intentionCreation={intentionCreation}
                    selectionInitiale={service}
                    visible={onglet === 'evenements'}
                  />
                </div>
              ) : null}
              {peut('services.lire') ? (
                <>
                  <div hidden={onglet !== 'formulaires'}>
                    <FormulairesPane
                      visible={onglet === 'formulaires'}
                      baseUrl={baseUrl}
                      permissions={permissions}
                      onAuth={() => setConnexionRequise(true)}
                    />
                  </div>
                  <div hidden={onglet !== 'documents'}>
                    <style>{stylesDocuments}</style>
                    <DocumentsPane
                      visible={onglet === 'documents'}
                      baseUrl={baseUrl}
                      permissions={permissions}
                      onAuth={() => setConnexionRequise(true)}
                    />
                  </div>
                </>
              ) : null}
              {peut('conversation.utiliser') ? (
                <div class="admin__panneau" hidden={onglet !== 'conversation'}>
                  <div class="admin__section ux-conversation-select">
                    <label for="admin-conversation">Historique</label>
                    <select
                      id="admin-conversation"
                      disabled={conversationEnCours}
                      value={conversationCourante}
                      onChange={(e) =>
                        setConversationCourante(e.currentTarget.value)
                      }
                    >
                      {!conversations.some(
                        (c) => c.id === conversationCourante,
                      ) ? (
                        <option value={conversationCourante}>
                          Nouvelle conversation
                        </option>
                      ) : null}
                      {conversations.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.titre}
                        </option>
                      ))}
                    </select>
                  </div>
                  <ConversationPane
                    key={conversationCourante}
                    client={client}
                    id={conversationCourante}
                    visible={onglet === 'conversation'}
                    actualiser={() => void actualiser()}
                    activite={setConversationEnCours}
                  />
                </div>
              ) : null}
            </>
          )}
          <footer class="ux-footer">
            <span>
              <strong>Funo</strong> · Une solution Harmonixus
            </span>
            <span>Simple, du début à la fin.</span>
          </footer>
        </main>
      </div>
      <div class="ux-toast" role="status" aria-live="polite">
        {notification ? (
          <span>
            <Icone nom="verifier" taille={18} />
            {notification}
            <button
              type="button"
              aria-label="Fermer la notification"
              onClick={() => setNotification('')}
            >
              <Icone nom="fermer" taille={15} />
            </button>
          </span>
        ) : null}
      </div>
      {rechercheOuverte ? (
        <RecherchePortail
          services={peut('services.lire') ? tableau.services : []}
          demandes={peut('demandes.lire') ? tableau.demandes : []}
          sections={sectionsVisibles.filter(
            (s) =>
              s.groupe ||
              ['conversation', 'formulaires', 'documents'].includes(s.cle),
          )}
          fermer={() => setRechercheOuverte(false)}
          naviguer={(cle) => {
            setRechercheOuverte(false)
            naviguer(cle)
          }}
          ouvrirEvenement={(cle) => {
            setRechercheOuverte(false)
            ouvrirEvenement(cle)
          }}
          ouvrirDemande={(id) => {
            setRechercheOuverte(false)
            setDetailId(id)
          }}
        />
      ) : null}
      {detailId && peut('demandes.lire') ? (
        <FicheDemande
          key={detailId}
          id={detailId}
          client={client}
          fuseau={fuseau}
          fermer={() => setDetailId(null)}
          actualiser={() => void actualiser()}
          connexion={() => setConnexionRequise(true)}
          gerer={peut('demandes.gerer')}
          revisionExterne={tableau}
          decider={(id, type, titre) => setAction({ id, type, titre })}
        />
      ) : null}
      <dialog
        ref={dialogue}
        class="admin__dialogue ux-confirmation"
        aria-labelledby="admin-dialogue-titre"
        onCancel={(e) => {
          if (mutation) e.preventDefault()
          else fermerDialogue()
        }}
        onClose={() => {
          setAction(null)
          setMotif('')
          setDemonstrationConfirmee(false)
        }}
      >
        <form onSubmit={confirmer}>
          <span class="ux-icone-douce">
            <Icone
              nom={action?.type === 'refuser' ? 'fermer' : 'verifier'}
              taille={26}
            />
          </span>
          <h2 id="admin-dialogue-titre">
            {action?.type === 'publier'
              ? 'Prêt à ouvrir les réservations ?'
              : action?.type === 'refuser'
                ? 'Refuser cette réservation ?'
                : 'Approuver cette réservation ?'}
          </h2>
          <p class="ux-confirmation-nom">{action?.titre}</p>
          <p>
            {action?.type === 'publier'
              ? 'La version que vous avez prévisualisée sera visible sur le portail public.'
              : action?.type === 'refuser'
                ? 'Le créneau sera libéré. Les remboursements prévus seront demandés.'
                : 'La personne passera à l’étape suivante de sa réservation.'}
          </p>
          {action?.type === 'refuser' ? (
            <div class="admin__champ">
              <label for="admin-motif">Votre message au demandeur</label>
              <textarea
                id="admin-motif"
                required
                value={motif}
                onInput={(e) => setMotif(e.currentTarget.value)}
                placeholder="Expliquez brièvement votre décision."
              />
            </div>
          ) : null}
          {publicationSimulee ? (
            <label class="admin__confirmation-demo">
              <input
                type="checkbox"
                checked={demonstrationConfirmee}
                onChange={(e) =>
                  setDemonstrationConfirmee(e.currentTarget.checked)
                }
                required
              />
              <span>
                Je confirme l’ouverture en démonstration. Aucun paiement réel ne
                sera encaissé.
              </span>
            </label>
          ) : null}
          {erreur ? (
            <p role="alert" class="admin__message--erreur">
              {erreur}
            </p>
          ) : null}
          <div class="admin__actions">
            <button
              class="admin__secondaire"
              type="button"
              disabled={mutation}
              onClick={fermerDialogue}
            >
              Retour
            </button>
            <button
              class={
                action?.type === 'refuser' ? 'admin__danger' : 'admin__primaire'
              }
              disabled={
                mutation ||
                (action?.type === 'refuser' && !motif.trim()) ||
                Boolean(publicationSimulee && !demonstrationConfirmee)
              }
              type="submit"
            >
              {mutation
                ? 'Enregistrement…'
                : action?.type === 'publier'
                  ? 'Publier l’événement'
                  : action?.type === 'refuser'
                    ? 'Confirmer le refus'
                    : 'Approuver'}
            </button>
          </div>
        </form>
      </dialog>
    </div>
  )
}

function RecherchePortail({
  services,
  demandes,
  sections,
  fermer,
  naviguer,
  ouvrirEvenement,
  ouvrirDemande,
}: {
  services: ServiceAdmin[]
  demandes: DemandeAdmin[]
  sections: typeof SECTIONS
  fermer(): void
  naviguer(cle: Onglet): void
  ouvrirEvenement(cle: string): void
  ouvrirDemande(id: string): void
}) {
  const [recherche, setRecherche] = useState('')
  const dialogue = useRef<HTMLDialogElement>(null),
    champ = useRef<HTMLInputElement>(null)
  useEffect(() => {
    dialogue.current?.showModal()
    champ.current?.focus()
    return () => dialogue.current?.close()
  }, [])
  const normaliser = (s: string) =>
    s
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
  const q = normaliser(recherche.trim())
  const evenements = q
    ? services
        .filter((s) =>
          normaliser(`${s.titre ?? ''} ${s.nom ?? ''} ${s.cle}`).includes(q),
        )
        .slice(0, 5)
    : []
  const reservations = q ? filtrerDemandes(demandes, q, '').slice(0, 5) : []
  const liens = sections.filter(
    (s) => !q || normaliser(`${s.titre} ${s.description}`).includes(q),
  )
  return (
    <dialog
      class="ux-recherche"
      ref={dialogue}
      onCancel={fermer}
      onClose={fermer}
      onKeyDown={(e) => {
        if (!['ArrowDown', 'ArrowUp'].includes(e.key)) return
        const boutons = Array.from(
          dialogue.current?.querySelectorAll<HTMLButtonElement>(
            '.ux-recherche-resultats button',
          ) ?? [],
        )
        if (!boutons.length) return
        e.preventDefault()
        const index = boutons.indexOf(
          document.activeElement as HTMLButtonElement,
        )
        boutons[
          (index + (e.key === 'ArrowDown' ? 1 : -1) + boutons.length) %
            boutons.length
        ]?.focus()
      }}
      aria-label="Rechercher dans le portail"
    >
      <div class="ux-recherche-champ">
        <Icone nom="recherche" />
        <input
          ref={champ}
          aria-label="Votre recherche"
          placeholder="Un événement, un nom, une action…"
          value={recherche}
          onInput={(e) => setRecherche(e.currentTarget.value)}
        />
        <button type="button" onClick={fermer} aria-label="Fermer la recherche">
          <kbd>esc</kbd>
        </button>
      </div>
      <div class="ux-recherche-resultats">
        {evenements.length ? (
          <>
            <h2>Événements</h2>
            {evenements.map((s) => (
              <button key={s.cle} onClick={() => ouvrirEvenement(s.cle)}>
                <Icone nom="evenement" />
                <span>{s.titre ?? s.nom ?? s.cle}</span>
                <Icone nom="chevron" taille={14} />
              </button>
            ))}
          </>
        ) : null}
        {reservations.length ? (
          <>
            <h2>Réservations</h2>
            {reservations.map((d) => (
              <button key={d.id} onClick={() => ouvrirDemande(d.id)}>
                <Icone nom="personnes" />
                <span>
                  {d.demandeur ?? d.courriel ?? 'Réservation'}
                  <small>
                    {d.ressource ?? d.service_cle} · {libelleEtat(d.etat)}
                  </small>
                </span>
                <Icone nom="chevron" taille={14} />
              </button>
            ))}
          </>
        ) : null}
        {liens.length ? (
          <>
            <h2>Aller à</h2>
            {liens.map((s) => (
              <button key={s.cle} onClick={() => naviguer(s.cle)}>
                <Icone nom={s.icone} />
                <span>{s.titre}</span>
                <Icone nom="chevron" taille={14} />
              </button>
            ))}
          </>
        ) : null}
        {!evenements.length && !reservations.length && !liens.length ? (
          <div class="ux-recherche-vide">
            <h2>Aucun résultat pour « {recherche} »</h2>
            <p>Essayez un autre nom ou le titre d’un événement.</p>
          </div>
        ) : null}
      </div>
      <footer>
        Parcourez avec ↑ ↓ ou Tab. Appuyez sur Entrée pour ouvrir.
      </footer>
    </dialog>
  )
}

function Connexion({ client, loginMode }: { client: ClientAdmin; loginMode?: 'central' | 'local' }) {
  const lienHarmonixus = '/auth/harmonixus/start?returnTo=' + encodeURIComponent(window.location.pathname + window.location.search + window.location.hash)
  useEffect(() => { if (loginMode === 'central') window.location.replace(lienHarmonixus) }, [loginMode])
  const [courriel, setCourriel] = useState(''),
    [motDePasse, setMotDePasse] = useState(''),
    [operation, setOperation] = useState<'connexion' | 'lien' | null>(null),
    [message, setMessage] = useState(''),
    [erreur, setErreur] = useState('')
  const connecter = async (event: Event) => {
    event.preventDefault()
    if (operation) return
    setOperation('connexion')
    setErreur('')
    setMessage('')
    try {
      await client.connecter(courriel.trim(), motDePasse)
      if (loginMode === 'local') window.location.assign('/admin')
      else window.location.reload()
    } catch (cause) {
      setErreur(erreurLisible(cause))
      setOperation(null)
    }
  }
  const envoyerLien = async () => {
    if (operation) return
    const champCourriel = document.getElementById(
      'admin-courriel-connexion',
    ) as HTMLInputElement | null
    if (!champCourriel?.checkValidity()) {
      champCourriel?.reportValidity()
      return
    }
    setOperation('lien')
    setErreur('')
    setMessage('')
    try {
      await client.demanderLien(courriel.trim())
      setMessage(
        'Si cette adresse appartient à un membre actif de l’équipe, vous recevrez un lien valable vingt minutes. Consultez votre boîte de réception.',
      )
    } catch (cause) {
      setErreur(erreurLisible(cause))
    } finally {
      setOperation(null)
    }
  }
  if (loginMode === 'central') return <main class="admin__connexion"><h1>Connexion Harmonixus</h1><p role="status">Redirection vers votre connexion sécurisée…</p><a href={lienHarmonixus}>Continuer avec Harmonixus</a></main>
  return (
    <main class="admin__connexion">
      {loginMode === 'local' ? <p class="admin__surtitre">HARMONIXUS</p> : <img
        class="ux-login-logo"
        src="/brand/funo-logo-vertical-couleur-fond-clair.svg"
        alt="Funo"
        width="168"
        height="162"
      />}
      <p class="admin__surtitre">ESPACE ÉQUIPE</p>
      <h1>{loginMode === 'local' ? 'Vos accès existants.' : 'Heureux de vous retrouver.'}</h1>
      <p>
        Connectez-vous avec les accès remis à votre équipe.
      </p>
      <form onSubmit={connecter}>
        <div class="admin__champ">
          <label for="admin-courriel-connexion">Votre adresse courriel</label>
          <input
            id="admin-courriel-connexion"
            type="email"
            autoComplete="email"
            required
            value={courriel}
            onInput={(e) => setCourriel(e.currentTarget.value)}
            placeholder="vous@votre-organisation.ca"
          />
        </div>
        <div class="admin__champ">
          <label for="admin-mot-de-passe">Votre mot de passe</label>
          <input
            id="admin-mot-de-passe"
            type="password"
            autoComplete="current-password"
            required
            maxLength={200}
            value={motDePasse}
            onInput={(e) => setMotDePasse(e.currentTarget.value)}
          />
        </div>
        <button
          class="admin__primaire"
          disabled={operation !== null}
          type="submit"
        >
          {operation === 'connexion' ? 'Connexion…' : 'Se connecter'}
        </button>
        <p>Vous préférez vous connecter sans mot de passe?</p>
        <button
          class="admin__secondaire"
          disabled={operation !== null}
          type="button"
          onClick={envoyerLien}
        >
          {operation === 'lien'
            ? 'Envoi…'
            : 'Recevoir un lien de connexion'}
        </button>
      </form>
      {message ? (
        <p class="admin__section" role="status">
          {message}
        </p>
      ) : null}
      {erreur ? (
        <p class="admin__message admin__message--erreur" role="alert">
          {erreur}
        </p>
      ) : null}
    </main>
  )
}

function Stat({
  titre,
  valeur,
  detail,
  icone,
  action,
}: {
  titre: string
  valeur: number
  detail: string
  icone: string
  action?: () => void
}) {
  const contenu = (
    <>
      <span class="admin__stat-titre">
        {titre}
        <Icone nom={icone} />
      </span>
      <strong>{valeur}</strong>
      <small>
        {detail}
        {action ? <Icone nom="fleche" taille={16} /> : null}
      </small>
    </>
  )
  return action ? (
    <button type="button" class="admin__stat" onClick={action}>
      {contenu}
    </button>
  ) : (
    <div class="admin__stat">{contenu}</div>
  )
}
function EtatVide({
  titre,
  texte,
  action,
  bouton,
}: {
  titre: string
  texte: string
  action?: () => void
  bouton?: string
}) {
  return (
    <div class="admin__vide">
      <span class="admin__icone-service">
        <Icone nom="calendrier" taille={28} />
      </span>
      <h3>{titre}</h3>
      <p>{texte}</p>
      {action ? (
        <button type="button" class="admin__secondaire" onClick={action}>
          {bouton}
        </button>
      ) : null}
    </div>
  )
}
function ConversationPane({
  client,
  id,
  visible,
  actualiser,
  activite,
}: {
  client: ClientAdmin
  id: string
  visible: boolean
  actualiser(): void
  activite(enCours: boolean): void
}) {
  const [etat, setEtat] = useState<EtatConversation>({
    messages: [],
    reponseEnCours: '',
    outils: [],
    erreur: null,
  })
  const [texte, setTexte] = useState(''),
    [envoi, setEnvoi] = useState(false)
  const fil = useRef<HTMLDivElement>(null),
    champ = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    const c = new AbortController()
    void client
      .conversation(id, c.signal)
      .then((conversation) => setEtat(depuisConversation(conversation)))
      .catch((cause) => {
        if (!c.signal.aborted)
          setEtat((e) => ({ ...e, erreur: erreurLisible(cause) }))
      })
    return () => c.abort()
  }, [client, id])
  useEffect(() => {
    if (visible && fil.current) fil.current.scrollTop = fil.current.scrollHeight
  }, [visible, etat.messages.length, etat.reponseEnCours])
  const recevoir = (evenement: EvenementConversation) =>
    setEtat((courant) => appliquerEvenement(courant, evenement))
  const envoyer = async (event: Event) => {
    event.preventDefault()
    const propre = texte.trim()
    if (!propre || envoi) return
    setTexte('')
    setEnvoi(true)
    activite(true)
    setEtat((e) => ({
      ...e,
      erreur: null,
      messages: [...e.messages, { role: 'responsable', texte: propre }],
    }))
    try {
      await client.envoyerMessage(id, propre, recevoir)
    } catch (cause) {
      recevoir({ type: 'erreur', message: erreurLisible(cause) })
      setTexte((courant) => courant || propre)
    } finally {
      setEnvoi(false)
      activite(false)
      actualiser()
    }
  }
  return (
    <div class="admin__conversation">
      <div
        class="admin__fil"
        ref={fil}
        role="log"
        aria-label="Conversation avec Funo"
        aria-live="polite"
      >
        {etat.messages.length ? (
          etat.messages.map((message, index) => (
            <article
              key={message.id ?? index}
              class={`admin__bulle admin__bulle--${message.role}`}
            >
              <strong>
                {message.role === 'responsable' ? 'Vous' : 'Funo'}
              </strong>
              <div>{message.texte}</div>
            </article>
          ))
        ) : (
          <div class="admin__bienvenue-assistant">
            <span class="admin__icone-service">
              <Icone nom="conversation" taille={30} />
            </span>
            <h2>Qu’aimeriez-vous mettre en place ?</h2>
            <p>
              Parlez de votre service, de vos horaires et de vos règles.
              <br />
              Nous préparerons un aperçu ensemble.
            </p>
            <div class="admin__suggestions">
              {[
                'Je veux ouvrir les réservations de ma salle communautaire.',
                'Je veux que les citoyens puissent réserver les terrains de pickleball.',
              ].map((exemple) => (
                <button
                  type="button"
                  class="admin__secondaire"
                  onClick={() => {
                    setTexte(exemple)
                    champ.current?.focus()
                  }}
                >
                  {exemple}
                  <Icone nom="fleche" taille={18} />
                </button>
              ))}
            </div>
          </div>
        )}
        {etat.outils.length ? (
          <details class="admin__outil">
            <summary>
              Voir les actions de l’assistant ({etat.outils.length})
            </summary>
            {etat.outils.map((outil, i) => (
              <p key={i}>
                {outil.nom}
                {outil.etat ? ` · ${outil.etat}` : ''}
                {outil.detail ? ` · ${outil.detail}` : ''}
              </p>
            ))}
          </details>
        ) : null}
        {etat.reponseEnCours ? (
          <div class="admin__bulle admin__bulle--assistant">
            {etat.reponseEnCours}
            <span aria-hidden="true"> ▍</span>
          </div>
        ) : null}
        {etat.erreur ? (
          <div class="admin__message admin__message--erreur" role="alert">
            {etat.erreur}
          </div>
        ) : null}
      </div>
      <form class="admin__compositeur" onSubmit={envoyer}>
        <label class="admin__visually-hidden" for="admin-message">
          Votre message
        </label>
        <textarea
          ref={champ}
          id="admin-message"
          value={texte}
          onInput={(e) => setTexte(e.currentTarget.value)}
          placeholder="Décrivez ce que vous souhaitez…"
        />
        <button
          class="admin__primaire"
          type="submit"
          disabled={envoi || !texte.trim()}
        >
          {envoi ? 'En cours…' : 'Envoyer'}
          <Icone nom="fleche" taille={18} />
        </button>
      </form>
    </div>
  )
}
function ApercuPane({
  baseUrl,
  service,
}: {
  baseUrl: string
  service: string
}) {
  const [format, setFormat] = useState('ordinateur')
  return service ? (
    <div class="admin__apercu">
      <div class="admin__formats" aria-label="Largeur de l’aperçu">
        {['ordinateur', 'mobile'].map((f) => (
          <button
            type="button"
            aria-pressed={format === f}
            onClick={() => setFormat(f)}
          >
            {f === 'mobile' ? 'Mobile' : 'Ordinateur'}
          </button>
        ))}
      </div>
      <iframe
        class={format === 'mobile' ? 'admin__iframe--mobile' : ''}
        title={`Aperçu de ${service}`}
        src={`${baseUrl.replace(/\/$/, '')}/apercu/${encodeURIComponent(service)}`}
      />
    </div>
  ) : (
    <EtatVide
      titre="Aucun service à prévisualiser"
      texte="Créez un service avec l’assistant pour essayer le parcours citoyen."
    />
  )
}
function IntegrationPane({ services }: { services: ServiceAdmin[] }) {
  const [copie, setCopie] = useState(''),
    [erreur, setErreur] = useState('')
  const publies = services.filter(
    (s) => s.etat === 'publie' && s.code_integration,
  )
  const copier = async (cle: string, code: string) => {
    try {
      await navigator.clipboard.writeText(code)
      setCopie(cle)
      setErreur('')
    } catch {
      setCopie('')
      setErreur(
        'La copie automatique est indisponible. Sélectionnez la ligne ci-dessous et copiez-la manuellement.',
      )
    }
  }
  return (
    <div class="admin__section">
      <h2>Votre réservation, sur votre site</h2>
      <p>
        Collez cette ligne à l’endroit voulu dans votre page, ou transmettez-la
        à la personne qui gère votre site. Le calendrier reprendra vos polices,
        vos couleurs et vos boutons.
      </p>
      <p role="status">
        {copie ? 'La ligne d’intégration a été copiée.' : erreur}
      </p>
      {publies.length ? (
        <div class="admin__grille">
          {publies.map((s) => (
            <article class="admin__carte" key={s.cle}>
              <h3>{s.titre ?? s.nom ?? s.cle}</h3>
              <pre class="admin__code" tabIndex={0}>
                <code>{s.code_integration}</code>
              </pre>
              <div class="admin__actions">
                <button
                  class="admin__primaire"
                  type="button"
                  onClick={() => void copier(s.cle, s.code_integration!)}
                >
                  {copie === s.cle ? 'Copié' : 'Copier la ligne'}
                </button>
                {s.url_publique ? (
                  <a href={s.url_publique} target="_blank" rel="noreferrer">
                    Voir la page publique <Icone nom="externe" taille={14} />
                  </a>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      ) : (
        <EtatVide
          titre="Publiez votre premier service"
          texte="Sa ligne d’intégration apparaîtra ici une fois la page en ligne."
        />
      )}
    </div>
  )
}
function TableDemandes({
  demandes,
  services,
  fuseau,
  mutation = false,
  decider,
  ouvrir,
}: {
  demandes: DemandeAdmin[]
  services: ServiceAdmin[]
  fuseau: string
  mutation?: boolean
  decider?(demande: DemandeAdmin, type: 'approuver' | 'refuser'): void
  ouvrir(id: string): void
}) {
  return demandes.length ? (
    <div
      class="admin__table-wrap"
      tabIndex={0}
      role="region"
      aria-label="Liste des demandes"
    >
      <table class="admin__table">
        <thead>
          <tr>
            <th scope="col">Demandeur</th>
            <th scope="col">Événement et créneau</th>
            <th scope="col">État</th>
            {decider ? <th scope="col">Actions</th> : null}
          </tr>
        </thead>
        <tbody>
          {demandes.map((d) => (
            <tr key={d.id}>
              <td>
                <button
                  type="button"
                  class="admin__nom-demande"
                  onClick={() => ouvrir(d.id)}
                  aria-label={`Voir la réservation de ${d.demandeur ?? d.courriel ?? 'ce demandeur'}`}
                >
                  {d.demandeur ?? d.courriel ?? 'Coordonnées à compléter'}
                </button>
                {d.demandeur && d.courriel ? <small>{d.courriel}</small> : null}
              </td>
              <td>
                <strong>
                  {services.find((s) => s.cle === d.service_cle)?.titre ??
                    d.service_cle ??
                    'Service'}
                </strong>
                <small>{periodeLisible(d, fuseau)}</small>
                {d.ressource ? <small>{d.ressource}</small> : null}
              </td>
              <td>
                <span class={`admin__etat admin__etat--${d.etat}`}>
                  {libelleEtat(d.etat)}
                </span>
              </td>
              {decider ? (
                <td>
                  {d.etat === 'en_attente_approbation' ? (
                    <div class="admin__actions">
                      <button
                        class="admin__primaire"
                        type="button"
                        disabled={mutation}
                        onClick={() => decider(d, 'approuver')}
                      >
                        Approuver
                      </button>
                      <button
                        class="admin__danger"
                        type="button"
                        disabled={mutation}
                        onClick={() => decider(d, 'refuser')}
                      >
                        Refuser
                      </button>
                    </div>
                  ) : (
                    <span class="admin__note">Aucune décision requise</span>
                  )}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <EtatVide
      titre="Aucune demande à afficher"
      texte="Les nouvelles demandes apparaîtront ici. Essayez d’autres filtres si une recherche est active."
    />
  )
}
function DemandesPane({
  demandes,
  services,
  initial,
  mutation,
  fuseau,
  decider,
  ouvrir,
}: {
  demandes: DemandeAdmin[]
  services: ServiceAdmin[]
  initial: string
  mutation: boolean
  fuseau: string
  decider?(d: DemandeAdmin, type: 'approuver' | 'refuser'): void
  ouvrir(id: string): void
}) {
  const [recherche, setRecherche] = useState(''),
    [etat, setEtat] = useState(initial),
    [service, setService] = useState('')
  const filtrees = filtrerDemandes(demandes, recherche, etat, service)
  return (
    <>
      <div class="admin__section">
        <div class="admin__filtres">
          <div class="admin__champ">
            <label for="admin-recherche">Rechercher une demande</label>
            <input
              id="admin-recherche"
              type="search"
              value={recherche}
              onInput={(e) => setRecherche(e.currentTarget.value)}
              placeholder="Nom, courriel, salle…"
            />
          </div>
          <div class="admin__champ">
            <label for="admin-etat">État</label>
            <select
              id="admin-etat"
              value={etat}
              onInput={(e) => setEtat(e.currentTarget.value)}
            >
              <option value="">Tous les états</option>
              {Object.entries(ETATS).map(([cle, titre]) => (
                <option value={cle}>{titre}</option>
              ))}
            </select>
          </div>
          <div class="admin__champ">
            <label for="admin-filtre-service">Service</label>
            <select
              id="admin-filtre-service"
              value={service}
              onInput={(e) => setService(e.currentTarget.value)}
            >
              <option value="">Tous les services</option>
              {services.map((s) => (
                <option value={s.cle}>{s.titre ?? s.nom ?? s.cle}</option>
              ))}
            </select>
          </div>
        </div>
        <div class="admin__resultat-filtre">
          <span role="status">
            {filtrees.length} résultat{filtrees.length > 1 ? 's' : ''} parmi les{' '}
            {demandes.length} demandes récentes (100 maximum).
          </span>
          {recherche || etat || service ? (
            <button
              type="button"
              class="admin__texte-bouton"
              onClick={() => {
                setRecherche('')
                setEtat('')
                setService('')
              }}
            >
              Effacer les filtres
            </button>
          ) : null}
        </div>
      </div>
      <TableDemandes
        demandes={filtrees}
        services={services}
        mutation={mutation}
        decider={decider}
        ouvrir={ouvrir}
        fuseau={fuseau}
      />
      <p class="admin__section admin__note">
        Les heures sont affichées dans le fuseau {fuseau}.
      </p>
    </>
  )
}
function ConsignesPane({
  consignes,
}: {
  consignes: TableauAdmin['consignes']
}) {
  return consignes.length ? (
    <div class="admin__grille">
      {consignes.map((c) => (
        <article class="admin__carte admin__carte--consigne" key={c.cle}>
          <span
            class={`admin__etat ${c.faite ? 'admin__etat--confirmee' : 'admin__etat--en_attente_approbation'}`}
          >
            {c.faite ? 'Terminé' : 'À faire'}
          </span>
          <h2>{c.titre}</h2>
          {c.pourquoi ? <p>{c.pourquoi}</p> : null}
          {c.etapes?.length ? (
            <ol class="admin__etapes">
              {c.etapes.map((e) => (
                <li>{e}</li>
              ))}
            </ol>
          ) : null}
          {c.verification ? (
            <p class="admin__note">Vérification : {c.verification}</p>
          ) : null}
        </article>
      ))}
    </div>
  ) : (
    <section class="admin__panneau">
      <EtatVide
        titre="Tout est configuré"
        texte="Aucune étape de configuration n’est signalée pour le moment."
      />
    </section>
  )
}
function monter(): void {
  installerStylesAdmin()
  document
    .querySelectorAll<HTMLElement>('[data-funo-admin]')
    .forEach((racine) => {
      if (racine.dataset.funoMonte === 'oui') return
      racine.dataset.funoMonte = 'oui'
      racine.replaceChildren()
      render(
        <App
          baseUrl={racine.dataset.funoBaseUrl ?? ''}
          conversationId={racine.dataset.funoConversation ?? 'principal'}
          loginMode={racine.dataset.funoLoginMode as 'central' | 'local' | undefined}
        />,
        racine,
      )
    })
}
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading')
    document.addEventListener('DOMContentLoaded', monter, { once: true })
  else monter()
}
